import { gql, useMutation } from "@apollo/client";
import { Box, Button, createStyles, makeStyles, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@material-ui/core";
import React from "react";
import { useKeysetPagination } from "../../apollo/networking/use-keyset-pagination";
import { useAppDialog } from "../../app-dialog";
import { AttendeeDiplomaFragment, AttendeeDiplomaModel } from "../../models/attendee-diploma.model";
import { DiplomaModel } from "../../models/diploma.model";
import { ColumnSelector } from "../../ui/column-selector";
import { LoadingButton } from "../../ui/loading-button";
import { DiplomaEditorDialog } from "../event-diplomas/diploma-editor/diploma-editor.dialog";
import { DiplomaAttendeeItem } from "./diploma-attendee-item";
import { ExportCertAttendees } from "./export-diploma-attendees";
import { DiplomaAttendeeCol, useDiplomaAttendeeColumns } from "./use-diploma-attendee-columns";



type Props = {
    diploma: DiplomaModel;
}


const MutationAssign = gql`
    mutation upsert_ac($object: attendee_diploma_insert_input!, $diploma_id: bigint!) {
        insert_attendee_diploma_one(object: $object, on_conflict: {
            constraint: attendee_diploma_pkey,
            update_columns: [deleted_at]
        }) {
            id
            attendee {
                ${AttendeeDiplomaFragment}
            }
        }
    }
`;


export const DiplomaAttendeeList = ({
    diploma: diploma,
}: Props) => {
    const classes = useStyles({});
    const app_dialog = useAppDialog();
    const [show_export, setShowExport] = React.useState(false);
    const {
        showColumn,
        columns,
        toggleColumn,
    } = useDiplomaAttendeeColumns();
    const [upsert] = useMutation(MutationAssign);
    const [show_edit, setShowEdit] = React.useState(false);
    const {
        items,
        loading,
        fetchMore,
        has_more,
    } = useKeysetPagination<AttendeeDiplomaModel>({
        entity_name: 'attendee',
        fragment: AttendeeDiplomaFragment,
        clause_outer: `
            $event_id: bigint!, 
            $diploma_id: bigint!
            $cursor: bigint!
        `,
        variables: {
            event_id: diploma.event_id,
            diploma_id: diploma.id,
            cursor: Number.MAX_SAFE_INTEGER,
        },
        query_inner: `
            where: {_and: [
                {ticket: {event_id: {_eq: $event_id}}},
                {id: {_lt: $cursor}},
            ]},
            order_by: {id: desc}
        `
    });

    const toggleAssign = async (item: AttendeeDiplomaModel, assign: boolean) => {
        try {
            await upsert({
                variables: {
                    diploma_id: diploma.id,
                    object: {
                        attendee_id: item.id,
                        diploma_id: diploma.id,
                        deleted_at: assign ? null : 'now()',
                    }
                }
            })
        } catch (e: any) {
            app_dialog.showError(e);
        }
    }

    const loadMore = () => {
        if (items.length === 0) {
            return;
        }
        fetchMore({
            event_id: diploma.event_id,
            certificate_id: diploma.id,
            cursor: items[items.length - 1].id,
        })
    }

    return <>
        {show_export ? <ExportCertAttendees
            certificate_id={diploma.id}
            event_id={diploma.event.id}
            event_title={diploma.event.title}
            onClose={() => setShowExport(false)}
        /> : null}
        {show_edit ? <DiplomaEditorDialog
            action='edit'
            event_id={diploma.event_id}
            item={diploma}
            onFinish={() => setShowEdit(false)}
            onClose={() => setShowEdit(false)}
        /> : null}
        <div className={classes.header}>
            <Button
                size='small'
                color='primary'
                onClick={() => setShowExport(true)}
                variant='contained'
                style={{
                    marginRight: 8,
                }}>
                Export
            </Button>
            <Button
                variant='outlined'
                size='small'
                onClick={() => setShowEdit(true)}>Edit Diploma</Button>

            <div style={{ flex: 1 }} />
            <ColumnSelector<DiplomaAttendeeCol>
                showColumn={showColumn}
                toggleColumn={toggleColumn}
                columns={columns}
            />
        </div>
        <TableContainer>
            <Table className={classes.table} size="small" aria-label="a dense table">
                <TableHead>
                    <TableRow className='header'>
                        <TableCell />
                        <TableCell>Id</TableCell>
                        <TableCell>Name</TableCell>
                        {showColumn('email') ? <TableCell>Email</TableCell> : null}
                        <TableCell>Assigned</TableCell>
                        <TableCell>Link</TableCell>
                        {showColumn('ticket') ? <TableCell>Ticket</TableCell> : null}
                        {showColumn('agd_id') ? <TableCell>AGD Id</TableCell> : null}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map((item) => <DiplomaAttendeeItem
                        key={item.id}
                        item={item}
                        showColumn={showColumn}
                        toggleAssign={toggleAssign}
                    />)}
                </TableBody>
            </Table>
        </TableContainer>
        {has_more ? <Box py={2}>
            <LoadingButton
                loading={loading}
                onClick={loadMore}
                fullWidth>Load More...</LoadingButton>
        </Box> : null}

    </>
}

const useStyles = makeStyles(theme => createStyles({
    table: {
        width: '100%',
        minWidth: 700,
        background: 'white',
        border: `1px solid ${theme.palette.divider}`,
        '& .header': {
            background: theme.palette.background.paper,
        }
    },
    header: {
        display: 'flex',
        paddingBottom: theme.spacing(2),
    }
}))